import cls from 'classnames';
import { useState, useEffect, Fragment } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useWindowSize } from 'usehooks-ts';

import theme from '../../../styles/theme';
import TabButton from '../base/TabButton2';

import useStyles, { GAP, MOBILE_GAP } from './styles';
import ITabSection from './types';

export function getDesktopMargin({
  tabIndex,
  imgWidth,
  gap,
}: {
  tabIndex: number;
  imgWidth: number;
  gap: number;
}) {
  if (tabIndex === 0) {
    return `calc((100% - ${imgWidth}px) / 2)`;
  }

  return `calc(((100% - ${imgWidth}px) / 2) - ${imgWidth * tabIndex}px - ${gap * tabIndex}px)`;
}

function getMobileMargin({
  tabIndex,
  smallImgWidth,
  gap,
}: {
  tabIndex: number;
  smallImgWidth: number;
  gap: number;
}) {
  if (tabIndex === 0) {
    0;
  }

  return `calc(((100% - ${smallImgWidth}px) / 2) - ${smallImgWidth * tabIndex}px - ${
    gap * tabIndex
  }px)`;
}

function TabSection({
  title,
  subtitle,
  items,
  titleClassName,
  tabsDescriptionClassName,
  titleElement = 'h2',
  imgWidth = 872,
  smallImgWidth = 327,
  className,
}: ITabSection) {
  const Tag = titleElement;
  const { width } = useWindowSize();
  const [tabIndex, setTabIndex] = useState(0);
  const [marginLeft, setMarginLeft] = useState('0');
  const classes = useStyles();

  useEffect(() => {
    const margin =
      width > theme.breakpoints.tablet
        ? getDesktopMargin({
            imgWidth,
            tabIndex,
            gap: GAP,
          })
        : getMobileMargin({ smallImgWidth, tabIndex, gap: MOBILE_GAP });
    setMarginLeft(margin);
  }, [width, tabIndex]);

  return (
    <section className={cls(classes.root, className, 'landing-banner')}>
      <Tag className={cls(classes.title, titleClassName)}>{title}</Tag>

      {subtitle && <p className={cls('fs-16', classes.subtitle)}>{subtitle}</p>}

      <div className={classes.tabs}>
        <div className={classes.tabButtons}>
          {items.map((item, i) => (
            <TabButton isSelected={tabIndex === i} index={i} key={i} onClick={setTabIndex}>
              {item.title}
            </TabButton>
          ))}
        </div>

        <div className={classes.tabPanels}>
          <div className={classes.tabContent}>
            <p className={cls('fs-16', tabsDescriptionClassName, classes.description)}>
              {items[tabIndex].description}
            </p>
            {
              <div className={classes.imgs}>
                <div
                  className={cls('flex', classes.imgContainer)}
                  style={{
                    marginLeft,
                  }}
                >
                  {items.map((item, i) => (
                    <Fragment key={`lli-${i}`}>
                      <LazyLoadImage
                        className={cls(classes.img, classes.desktopImg)}
                        src={item.src}
                        sizes={`${imgWidth}px`}
                        srcSet={`${item.src} ${imgWidth}w`}
                        alt={item.alt}
                        threshold={200}
                      />
                      <LazyLoadImage
                        className={cls(classes.img, classes.mobileImg)}
                        src={item.src}
                        sizes={`${smallImgWidth}px`}
                        srcSet={`${item.mobileSrc} ${smallImgWidth}w`}
                        alt={item.alt}
                        threshold={200}
                      />
                    </Fragment>
                  ))}
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </section>
  );
}

export default TabSection;
